import { Link } from "@tanstack/react-router";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "@/lib/utils";

const base =
  "inline-flex items-center justify-center gap-2 rounded-sm px-6 text-[0.78rem] font-semibold uppercase tracking-[0.14em] transition-all duration-200 focus-visible:outline-none disabled:opacity-50";

const variants = {
  green:
    "h-12 bg-nook-green text-nook-green-foreground hover:brightness-110 hover:-translate-y-[1px]",
  outline:
    "h-12 border border-border bg-transparent text-foreground hover:border-nook-green hover:text-nook-green",
  ghost:
    "h-11 border-b border-nook-green/60 rounded-none px-0 text-foreground hover:text-nook-green",
  light:
    "h-12 bg-offwhite text-offwhite-foreground hover:brightness-95",
} as const;

type Variant = keyof typeof variants;

export function ActionLink({
  to,
  href,
  variant = "green",
  className,
  children,
  ...rest
}: {
  to?: string;
  href?: string;
  variant?: Variant;
  className?: string;
  children: ReactNode;
} & Omit<ComponentProps<"a">, "href">) {
  const classes = cn(base, variants[variant], className);
  if (to) {
    return (
      <Link to={to} className={classes} {...(rest as object)}>
        {children}
      </Link>
    );
  }
  if (!href) {
    return (
      <button type="button" className={classes} {...(rest as ComponentProps<"button">)}>
        {children}
      </button>
    );
  }
  return (
    <a href={href} className={classes} target="_blank" rel="noreferrer" {...rest}>
      {children}
    </a>
  );
}
